import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { cn } from "../../ui/utils/cn";

const MAX_LINES = 3;

export interface ChatTextareaProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onChange" | "style" | "ref"
  > {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
}

function mergeRefs<T>(
  a: React.Ref<T> | undefined,
  b: React.RefObject<T | null>,
) {
  return (node: T | null) => {
    (b as React.MutableRefObject<T | null>).current = node;
    if (typeof a === "function") a(node);
    else if (a && "current" in a) {
      (a as React.MutableRefObject<T | null>).current = node;
    }
  };
}

/**
 * Auto-growing textarea (up to MAX_LINES) using the native scrollHeight.
 *
 * Height is written straight to the DOM in a layout effect — no per-keystroke
 * React state updates, no JS text measuring, and no ResizeObserver feedback
 * loop. That keeps typing smooth on mobile, where the old measure-and-setState
 * approach janked on every keypress and re-fired when the keyboard opened.
 */
export const ChatTextarea = React.forwardRef<HTMLTextAreaElement, ChatTextareaProps>(
  function ChatTextarea(
    { value, onChange, onSubmit, className, disabled, ...rest },
    forwardedRef,
  ) {
    const taRef = useRef<HTMLTextAreaElement>(null);
    const lastWidthRef = useRef(0);

    const resize = useCallback(() => {
      const ta = taRef.current;
      if (!ta) return;
      // collapse first so scrollHeight reflects the true content height
      ta.style.height = "auto";
      const cs = window.getComputedStyle(ta);
      const lineHeight = parseFloat(cs.lineHeight) || 20;
      const padY =
        parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const borderY =
        parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const maxH = lineHeight * MAX_LINES + padY + borderY;
      const next = Math.min(ta.scrollHeight, maxH);
      ta.style.height = `${next}px`;
      ta.style.overflowY = ta.scrollHeight > maxH ? "auto" : "hidden";
    }, []);

    // resize before paint so the box never flickers as you type
    useLayoutEffect(() => {
      resize();
    }, [value, resize]);

    // re-measure only when the *width* changes (orientation, sheet open, etc.).
    // ignore our own height writes to avoid an infinite ResizeObserver loop.
    useEffect(() => {
      const ta = taRef.current;
      if (!ta) return;
      lastWidthRef.current = ta.clientWidth;
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? ta.clientWidth;
        if (Math.abs(w - lastWidthRef.current) < 1) return;
        lastWidthRef.current = w;
        resize();
      });
      ro.observe(ta);
      return () => ro.disconnect();
    }, [resize]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit?.();
      }
    };

    return (
      <div className="relative flex-1 min-w-0">
        <textarea
          ref={mergeRefs(forwardedRef, taRef)}
          value={value}
          disabled={disabled}
          rows={1}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(
            "flex w-full min-h-0 resize-none rounded-none border-brutal border-ink bg-background px-3 py-2 text-sm",
            "ring-offset-background placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50 shadow-inset",
            // keep mobile font at 16px — anything smaller makes iOS Safari auto-zoom the
            // page on focus, which is the main reason typing on iPhone felt janky.
            "max-[700px]:text-[16px] max-[700px]:py-1.5",
            className,
          )}
          {...rest}
        />
      </div>
    );
  },
);

ChatTextarea.displayName = "ChatTextarea";
